import React, { useCallback, useEffect, useState } from "react";
import { Factory, BellRing, Send, X } from "lucide-react";
import { Link } from "react-router-dom";
import CrudModule, { useEmployees } from "@/components/CrudModule";
import { Badge2 } from "@/components/ComplianceBadge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const statusOf = (exp) => {
  if (!exp) return { color: "slate", label: "—" };
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(exp + "T00:00:00") - t) / 86400000);
  if (days < 0) return { color: "red", label: `Vencida há ${-days}d` };
  if (days === 0) return { color: "red", label: "Vence hoje" };
  if (days <= 60) return { color: "yellow", label: `Vence em ${days}d` };
  return { color: "green", label: "Válida" };
};

const COUNT_STYLE = { green: "bg-emerald-50 text-emerald-700", yellow: "bg-amber-50 text-amber-700", red: "bg-rose-50 text-rose-700" };

const PortalSummary = ({ portals, refresh, selected, onSelect }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portal-integrations").then(({ data }) => setItems(data)).catch(() => {}); }, [refresh]);
  const names = [...new Set([...portals.map((p) => p.name), ...items.map((i) => i.portal)])].filter(Boolean);
  if (!names.length) return null;
  return (
    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="portal-summary">
      {names.map((n, idx) => {
        const c = { green: 0, yellow: 0, red: 0 };
        items.filter((i) => i.portal === n).forEach((i) => { const s = statusOf(i.expires_at).color; if (c[s] !== undefined) c[s] += 1; });
        const client = portals.find((p) => p.name === n)?.client;
        return (
          <button type="button" key={n} onClick={() => onSelect(selected === n ? null : n)} data-testid={`portal-summary-card-${idx}`}
            className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-[box-shadow,border-color] hover:shadow-md ${selected === n ? "border-emerald-500 ring-2 ring-emerald-500/30" : "border-[#E2E8F0]"}`}>
            <p className="flex items-center gap-2 font-semibold text-[#0B2545]"><Factory className="h-4 w-4 text-emerald-600" /> {n}</p>
            {client && <p className="text-xs text-slate-500">{client}</p>}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {[["green", "Liberados"], ["yellow", "A vencer"], ["red", "Vencidos"]].map(([k, l]) => (
                <div key={k} className={`rounded-lg py-2 ${COUNT_STYLE[k]}`} data-testid={`portal-summary-${idx}-${k}`}>
                  <p className="text-xl font-bold">{c[k]}</p><p className="text-[11px] font-medium">{l}</p>
                </div>
              ))}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const AlertSettings = () => {
  const { canManage } = useAuth();
  const sendNow = async () => {
    try {
      const { data } = await api.post("/portal-integrations/send-alerts");
      toast.success(data.sent_items ? `Aviso enviado com ${data.sent_items} integração(ões)` : "Nenhuma integração nova para avisar agora");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4 sm:flex-row sm:items-center" data-testid="portal-alert-settings">
      <div className="flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><BellRing className="h-4 w-4" /> Avisos automáticos: 60, 30 e 7 dias antes e no dia do vencimento</p>
        <p className="mt-1 text-xs text-amber-700">Destinatários: administradores + e-mails cadastrados em <Link to="/configuracoes" className="font-semibold underline" data-testid="portal-alert-settings-link">Configurações → E-mails de alerta</Link>.</p>
      </div>
      {canManage && <Button variant="outline" onClick={sendNow} className="bg-white" data-testid="portal-alert-send-now"><Send className="mr-1.5 h-4 w-4" /> Enviar avisos agora</Button>}
    </div>
  );
};

export default function Portals() {
  const employees = useEmployees();
  const [portals, setPortals] = useState([]);
  const [tab, setTab] = useState("integracoes");
  const [selected, setSelected] = useState(null);
  const rowFilter = useCallback((r) => !selected || r.portal === selected, [selected]);
  useEffect(() => { api.get("/portals").then(({ data }) => setPortals(data)).catch(() => {}); }, [tab]);
  const pick = (n) => { setSelected(n); if (n) setTab("integracoes"); };

  return (
    <div data-testid="portals-page">
      <PortalSummary portals={portals} refresh={tab} selected={selected} onSelect={pick} />
      {selected && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="portal-filter-active">
          Mostrando só: <b>{selected}</b>
          <button type="button" onClick={() => setSelected(null)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold hover:underline" data-testid="portal-filter-clear"><X className="h-3.5 w-3.5" /> Ver todas as plantas</button>
        </div>
      )}
      <AlertSettings />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="integracoes" data-testid="portals-tab-integrations">Integrações</TabsTrigger>
          <TabsTrigger value="portais" data-testid="portals-tab-portals">Portais / Plantas</TabsTrigger>
        </TabsList>
        <TabsContent value="integracoes">
          <CrudModule title="Portais — Integrações" subtitle="Integrações dos colaboradores nas plantas dos clientes" icon={Factory}
            endpoint="portal-integrations" testid="portal-integrations" employees={employees} rowFilter={rowFilter}
            columns={[
              { key: "employee_name", label: "Colaborador" },
              { key: "portal", label: "Portal / Planta" },
              { key: "integration_date", label: "Integração" },
              { key: "validity_months", label: "Validade (meses)" },
              { key: "expires_at", label: "Vencimento" },
              { key: "status", label: "Situação", render: (r) => { const s = statusOf(r.expires_at); return <Badge2 color={s.color} testid={`portal-status-${r.id}`}>{s.label}</Badge2>; } },
            ]}
            fields={[
              { name: "portal", label: "Portal / Planta", type: "select", options: portals.map((p) => p.name), required: true, full: true },
              { name: "employee", label: "Colaborador", type: "employee", idKey: "employee_id", nameKey: "employee_name", full: true },
              { name: "integration_date", label: "Data da integração", type: "date", required: true },
              { name: "validity_months", label: "Validade (meses)", type: "number", default: 12 },
              { name: "notes", label: "Observações", type: "textarea", full: true },
            ]} />
          {portals.length === 0 && <p className="mt-3 text-sm text-amber-700" data-testid="portals-empty-hint">Cadastre primeiro um portal na aba "Portais / Plantas".</p>}
        </TabsContent>
        <TabsContent value="portais">
          <CrudModule title="Portais / Plantas" subtitle="Clientes e portais onde os colaboradores precisam estar cadastrados" icon={Factory}
            endpoint="portals" testid="portals"
            columns={[
              { key: "name", label: "Portal / Planta" },
              { key: "client", label: "Cliente" },
              { key: "url", label: "Link do portal", render: (r) => r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="text-[#134074] hover:underline">Abrir</a> : "—" },
              { key: "contact", label: "Contato" },
            ]}
            fields={[
              { name: "name", label: "Nome do portal / planta", type: "text", required: true, full: true },
              { name: "client", label: "Cliente", type: "text" },
              { name: "contact", label: "Contato", type: "text" },
              { name: "url", label: "Link do portal", type: "text", full: true },
              { name: "notes", label: "Observações", type: "textarea", full: true },
            ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
